import { useState, useEffect } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'

interface Depot {
  id: number
  name: string
  code: string | null
  address: string | null
  city: string | null
  postcode: string | null
  region: string | null
  manager_user_id: number | null
  manager_name: string | null
  phone: string | null
  driver_count: number
  vehicle_count: number
}

export function DepotsPage() {
  const [depots, setDepots] = useState<Depot[]>([])
  const [managers, setManagers] = useState<any[]>([])
  const [drivers, setDrivers] = useState<any[]>([])
  const [vehicles, setVehicles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingDepot, setEditingDepot] = useState<Depot | null>(null)
  const [selectedDepot, setSelectedDepot] = useState<Depot | null>(null)
  const [assignTab, setAssignTab] = useState<'drivers' | 'vehicles'>('drivers')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form state
  const [form, setForm] = useState({
    name: '', code: '', address: '', city: '', postcode: '', region: '', manager_user_id: '', phone: '',
  })

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    setLoading(true)
    try {
      const [d, m, dr, v] = await Promise.all([
        api.getDepots().catch(() => []),
        api.getManagers().catch(() => []),
        api.getDrivers({ page_size: 500 }).then(r => Array.isArray(r) ? r : r.drivers || []).catch(() => []),
        api.getVehicles().catch(() => []),
      ])
      setDepots(d)
      setManagers(m)
      setDrivers(dr)
      setVehicles(v)
    } catch (e: any) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  function openCreateForm() {
    setEditingDepot(null)
    setForm({ name: '', code: '', address: '', city: '', postcode: '', region: '', manager_user_id: '', phone: '' })
    setShowForm(true)
  }

  function openEditForm(depot: Depot) {
    setEditingDepot(depot)
    setForm({
      name: depot.name,
      code: depot.code || '',
      address: depot.address || '',
      city: depot.city || '',
      postcode: depot.postcode || '',
      region: depot.region || '',
      manager_user_id: depot.manager_user_id ? String(depot.manager_user_id) : '',
      phone: depot.phone || '',
    })
    setShowForm(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')
    const payload = {
      ...form,
      manager_user_id: form.manager_user_id ? Number(form.manager_user_id) : null,
    }
    try {
      if (editingDepot) {
        await api.updateDepot(editingDepot.id, payload)
        setSuccess(`"${form.name}" updated`)
      } else {
        await api.createDepot(payload)
        setSuccess(`"${form.name}" created`)
      }
      setShowForm(false)
      loadAll()
    } catch (e: any) {
      setError(errorMsg(e))
    }
  }

  async function handleDelete(depot: Depot) {
    if (!confirm(`Deactivate "${depot.name}"? Drivers and vehicles will be unassigned from this depot.`)) return
    setError('')
    setSuccess('')
    try {
      await api.deleteDepot(depot.id)
      setSuccess(`"${depot.name}" deactivated`)
      if (selectedDepot?.id === depot.id) setSelectedDepot(null)
      loadAll()
    } catch (e: any) {
      setError(errorMsg(e))
    }
  }

  function errorMsg(e: any): string {
    if (typeof e === 'string') return e
    if (e?.message && typeof e.message === 'string') return e.message
    if (e?.detail && typeof e.detail === 'string') return e.detail
    try { return JSON.stringify(e) } catch { return 'Unknown error' }
  }

  async function handleAssignDrivers(driverIds: number[], depotId: number | null) {
    setError('')
    setSuccess('')
    try {
      await api.assignDriversToDepot(driverIds, depotId)
      setSuccess(`${driverIds.length} driver(s) ${depotId ? 'assigned' : 'unassigned'}`)
      await loadAll()
    } catch (e: any) {
      setError(errorMsg(e))
    }
  }

  async function handleAssignVehicles(vehicleIds: number[], depotId: number | null) {
    setError('')
    setSuccess('')
    try {
      await api.assignVehiclesToDepot(vehicleIds, depotId)
      setSuccess(`${vehicleIds.length} vehicle(s) ${depotId ? 'assigned' : 'unassigned'}`)
      await loadAll()
    } catch (e: any) {
      setError(errorMsg(e))
    }
  }

  // Drivers/vehicles in selected depot
  const depotDrivers = selectedDepot ? drivers.filter(d => d.depot_id === selectedDepot.id) : []
  const depotVehicles = selectedDepot ? vehicles.filter(v => v.depot_id === selectedDepot.id) : []
  const unassignedDrivers = drivers.filter(d => !d.depot_id)
  const unassignedVehicles = vehicles.filter(v => !v.depot_id)

  return (
    <PortalLayout currentPage="depots">
      <div className="p-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900">Depot Management</h1>
            <p className="text-gray-500 mt-1">Manage locations and assign drivers and vehicles to depots</p>
          </div>
          <button
            onClick={openCreateForm}
            className="px-4 py-2 bg-slate-700 text-white rounded text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            + Add Depot
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
            {error}
            <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded p-3">
            {success}
            <button onClick={() => setSuccess('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full mx-auto mb-3" />
            Loading depots...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Depot list */}
            <div className="lg:col-span-1 space-y-3">
              {depots.length === 0 ? (
                <div className="bg-white rounded border border-gray-200 p-8 text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm mb-3">No depots yet</p>
                  <button onClick={openCreateForm} className="text-blue-600 text-sm font-medium hover:underline">
                    Create your first depot
                  </button>
                </div>
              ) : (
                depots.map(depot => (
                  <div
                    key={depot.id}
                    onClick={() => setSelectedDepot(depot)}
                    className={`bg-white rounded border p-4 cursor-pointer transition-all hover:shadow-sm ${
                      selectedDepot?.id === depot.id ? 'border-slate-500 ring-2 ring-slate-100' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{depot.name}</h3>
                        {depot.code && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{depot.code}</span>}
                        {depot.city && <p className="text-sm text-gray-500 mt-1">{depot.city}{depot.postcode ? `, ${depot.postcode}` : ''}</p>}
                        {depot.manager_name && <p className="text-xs text-gray-400 mt-1">Manager: {depot.manager_name}</p>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); openEditForm(depot) }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(depot) }} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-xs">
                      <span className="flex items-center gap-1 text-gray-500">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        {depot.driver_count} drivers
                      </span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                        {depot.vehicle_count} vehicles
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Right: Depot detail + assignment */}
            <div className="lg:col-span-2">
              {selectedDepot ? (
                <div className="space-y-4">
                  {/* Depot header */}
                  <div className="bg-white rounded border border-gray-200 p-5">
                    <h2 className="text-lg font-bold text-gray-900">{selectedDepot.name}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                      {selectedDepot.city && <InfoItem label="City" value={selectedDepot.city} />}
                      {selectedDepot.postcode && <InfoItem label="Postcode" value={selectedDepot.postcode} />}
                      {selectedDepot.region && <InfoItem label="Region" value={selectedDepot.region} />}
                      {selectedDepot.phone && <InfoItem label="Phone" value={selectedDepot.phone} />}
                      {selectedDepot.manager_name && <InfoItem label="Manager" value={selectedDepot.manager_name} />}
                    </div>
                  </div>

                  {/* Tab toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAssignTab('drivers')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                        assignTab === 'drivers' ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 border border-gray-200'
                      }`}
                    >
                      Drivers ({depotDrivers.length})
                    </button>
                    <button
                      onClick={() => setAssignTab('vehicles')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                        assignTab === 'vehicles' ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 border border-gray-200'
                      }`}
                    >
                      Vehicles ({depotVehicles.length})
                    </button>
                  </div>

                  {/* Assigned list */}
                  <div className="bg-white rounded border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      {assignTab === 'drivers' ? 'Assigned Drivers' : 'Assigned Vehicles'}
                    </h3>

                    {assignTab === 'drivers' ? (
                      depotDrivers.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No drivers assigned to this depot</p>
                      ) : (
                        <div className="space-y-2">
                          {depotDrivers.map((d: any) => (
                            <div key={d.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                              <div>
                                <p className="text-sm font-medium">{d.first_names} {d.surname}</p>
                                <p className="text-xs text-gray-500">{d.card_number}</p>
                              </div>
                              <button
                                onClick={() => handleAssignDrivers([d.id], null)}
                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      depotVehicles.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No vehicles assigned to this depot</p>
                      ) : (
                        <div className="space-y-2">
                          {depotVehicles.map((v: any) => (
                            <div key={v.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                              <div>
                                <p className="text-sm font-medium">{v.registration}</p>
                                <p className="text-xs text-gray-500">{v.make} {v.model}</p>
                              </div>
                              <button
                                onClick={() => handleAssignVehicles([v.id], null)}
                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>

                  {/* Unassigned list — available to add */}
                  {assignTab === 'drivers' && unassignedDrivers.length > 0 && (
                    <div className="bg-white rounded border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Unassigned Drivers</h3>
                      <div className="space-y-2">
                        {unassignedDrivers.map((d: any) => (
                          <div key={d.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div>
                              <p className="text-sm font-medium">{d.first_names} {d.surname}</p>
                              <p className="text-xs text-gray-500">{d.card_number}</p>
                            </div>
                            <button
                              onClick={() => handleAssignDrivers([d.id], selectedDepot.id)}
                              className="text-xs bg-slate-700 text-white px-3 py-1 rounded font-medium hover:bg-slate-800"
                            >
                              Add to {selectedDepot.name}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {assignTab === 'vehicles' && unassignedVehicles.length > 0 && (
                    <div className="bg-white rounded border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Unassigned Vehicles</h3>
                      <div className="space-y-2">
                        {unassignedVehicles.map((v: any) => (
                          <div key={v.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div>
                              <p className="text-sm font-medium">{v.registration}</p>
                              <p className="text-xs text-gray-500">{v.make} {v.model}</p>
                            </div>
                            <button
                              onClick={() => handleAssignVehicles([v.id], selectedDepot.id)}
                              className="text-xs bg-slate-700 text-white px-3 py-1 rounded font-medium hover:bg-slate-800"
                            >
                              Add to {selectedDepot.name}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded border border-gray-200 p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm">Select a depot to manage its drivers and vehicles</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded shadow-xl w-full max-w-lg">
              <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">{editingDepot ? 'Edit Depot' : 'Create Depot'}</h2>
              </div>
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Depot Name *</label>
                    <input
                      type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Birmingham Hub"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                      placeholder="e.g. BHX"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                      placeholder="Street address"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                      type="text" value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                    <input
                      type="text" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })}
                      placeholder="e.g. West Midlands"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Depot Manager</label>
                    <select
                      value={form.manager_user_id} onChange={e => setForm({ ...form, manager_user_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-slate-500"
                    >
                      <option value="">No manager assigned</option>
                      {managers.map((m: any) => (
                        <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-slate-700 text-white rounded text-sm font-medium hover:bg-slate-800">
                    {editingDepot ? 'Save Changes' : 'Create Depot'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-700">{value}</p>
    </div>
  )
}
